import { ProductProgress, ProductState, ProductStatus } from '@lemoncloud/codes-goods-api';

import { Badge } from '@codes/ui-kit';

interface StatusConfig {
    className: string;
    label: string;
}

const STATUS_STYLES = {
    IN_PROGRESS: 'bg-[#FFF0E7] text-point',
    SUCCESS: 'bg-[#E0FFE4] text-[#0B731A]',
    ERROR: 'bg-[#FFEEEA] text-error',
    IDLE: 'bg-default text-black',
    APPROVED: 'bg-[#E6EEFF] text-link',
} as const;

const DEFAULT_STATUS: StatusConfig = {
    className: STATUS_STYLES.IDLE,
    label: 'Unknown',
};

const PROGRESS_STATUS_MAP: Record<string, StatusConfig> = {
    'github.pending': { className: STATUS_STYLES.IN_PROGRESS, label: 'Downloading' },
    'github.running': { className: STATUS_STYLES.IN_PROGRESS, label: 'Downloading' },
    'build.pending': { className: STATUS_STYLES.IN_PROGRESS, label: 'Building' },
    'build.running': { className: STATUS_STYLES.IN_PROGRESS, label: 'Building' },
    'deploy.pending': { className: STATUS_STYLES.IN_PROGRESS, label: 'Deploying' },
    'deploy.running': { className: STATUS_STYLES.IN_PROGRESS, label: 'Deploying' },
    'deploy.success': { className: STATUS_STYLES.SUCCESS, label: 'In Use' },
};

const PRODUCT_STATUS_MAP: Record<ProductStatus, StatusConfig> = {
    busy: { className: STATUS_STYLES.IN_PROGRESS, label: 'Preparing' },
    running: { className: STATUS_STYLES.SUCCESS, label: 'In Use' },
    stopped: { className: STATUS_STYLES.IDLE, label: 'Stopped' },
    failure: { className: STATUS_STYLES.ERROR, label: 'Failed' },
    idle: { className: STATUS_STYLES.IDLE, label: 'Pending' },
    '': DEFAULT_STATUS,
};

const PRODUCT_STATE_MAP: Record<ProductState, StatusConfig> = {
    init: { className: STATUS_STYLES.IDLE, label: 'Pending' },
    rejected: { className: STATUS_STYLES.ERROR, label: 'Rejected' },
    approved: { className: STATUS_STYLES.APPROVED, label: 'Approved' },
    assigned: { className: STATUS_STYLES.APPROVED, label: 'Approved' },
    requested: { className: STATUS_STYLES.IN_PROGRESS, label: 'Requested' },
    deployed: { className: STATUS_STYLES.SUCCESS, label: 'Deployed' },
    error: { className: STATUS_STYLES.ERROR, label: 'Error' },
    canceled: { className: STATUS_STYLES.ERROR, label: 'Cancelled' },
    '': DEFAULT_STATUS,
};

interface ProductCardStatusProps {
    progress?: ProductProgress;
    status?: ProductStatus;
    state?: ProductState;
}

const getStatusConfig = (progress?: ProductProgress, status?: ProductStatus, state?: ProductState): StatusConfig => {
    if (progress?.code && PROGRESS_STATUS_MAP[progress.code]) {
        return PROGRESS_STATUS_MAP[progress.code];
    }

    if (progress?.status === 'failure') {
        return { className: STATUS_STYLES.ERROR, label: 'Failed' };
    }

    if (status && PRODUCT_STATUS_MAP[status]) {
        return PRODUCT_STATUS_MAP[status];
    }

    if (state && PRODUCT_STATE_MAP[state]) {
        return PRODUCT_STATE_MAP[state];
    }

    return DEFAULT_STATUS;
};

export const ProductCardStatus = ({ progress, status, state }: ProductCardStatusProps) => {
    const config = getStatusConfig(progress, status, state);

    return (
        <Badge variant="service" className={config.className}>
            {config.label}
        </Badge>
    );
};
